<template>
  <div class="p-6">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-600 dark:text-gray-100">Request Filter</h1>
      <button
        class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200 flex items-center space-x-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400"
        @click="openCreateModal"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
            clip-rule="evenodd"
          />
        </svg>
        <span>Create Filter</span>
      </button>
    </div>

    <!-- Search Box -->
    <div class="mb-6">
      <div class="max-w-md">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            class="w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            placeholder="Search by path, version, or filter values..."
          />
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              class="h-5 w-5 text-gray-400 dark:text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="rounded-xl shadow-lg border p-6 bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700"
    >
      <div class="flex items-center justify-center py-12">
        <div
          class="w-8 h-8 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"
        ></div>
        <span class="ml-3 text-gray-600 dark:text-gray-300">Loading filters...</span>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="rounded-xl shadow-lg border p-6 bg-white dark:bg-gray-800 border-red-100 dark:border-red-800"
    >
      <div class="flex items-center justify-center py-12 text-red-600 dark:text-red-400">
        <span>{{ error }}</span>
      </div>
    </div>

    <!-- Content -->
    <div
      v-else
      class="rounded-xl shadow-lg border bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/90 overflow-hidden"
    >
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700/40">
          <thead class="bg-gray-50 dark:bg-gray-800/80 rounded-t-xl">
            <tr>
              <th
                v-for="column in columns"
                :key="column.field"
                class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 first:rounded-tl-xl last:rounded-tr-xl"
                :class="{ 'cursor-pointer': column.sortable }"
                @click="column.sortable && handleSort(column.field)"
              >
                <div class="flex items-center space-x-1">
                  <span>{{ column.label }}</span>
                  <span
                    v-if="column.sortable"
                    class="flex flex-col"
                  >
                    <svg
                      class="w-3 h-3 transform"
                      :class="
                        sortField === column.field && sortOrder === 'asc'
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-400 dark:text-gray-500'
                      "
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                    <svg
                      class="w-3 h-3 transform"
                      :class="
                        sortField === column.field && sortOrder === 'desc'
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-400 dark:text-gray-500'
                      "
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody
            class="bg-white dark:bg-gray-800/90 divide-y divide-gray-200 dark:divide-gray-700/30"
          >
            <template
              v-for="item in paginatedData"
              :key="item._id"
            >
              <!-- Main Row -->
              <tr
                class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/70 transition-colors duration-200"
                @click="toggleExpand(item._id)"
              >
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600 dark:text-gray-200"
                >
                  {{ item.path }}
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600 dark:text-gray-200"
                >
                  {{ item.version }}
                </td>
                <td class="px-6 py-4 text-sm">
                  <div class="text-gray-600 dark:text-gray-300">
                    <template v-if="item.filter">
                      <span
                        v-if="Object.keys(item.filter.body || {}).length"
                        class="inline-block px-2 py-0.5 rounded mr-1 text-xs bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200"
                        >Body</span
                      >
                      <span
                        v-if="Object.keys(item.filter.query || {}).length"
                        class="inline-block px-2 py-0.5 rounded mr-1 text-xs bg-green-50 dark:bg-green-900 text-green-700 dark:text-green-200"
                        >Query</span
                      >
                      <span
                        v-if="Object.keys(item.filter.params || {}).length"
                        class="inline-block px-2 py-0.5 rounded mr-1 text-xs bg-yellow-50 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-200"
                        >Params</span
                      >
                      <span
                        v-if="Object.keys(item.filter.headers || {}).length"
                        class="inline-block px-2 py-0.5 rounded mr-1 text-xs bg-purple-50 dark:bg-purple-900 text-purple-700 dark:text-purple-200"
                        >Headers</span
                      >
                    </template>
                    <span v-else>-</span>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                  {{ formatDate(item.createdAt) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                  {{ formatDate(item.updatedAt) }}
                </td>
                <!-- Add isWildcardPath column -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <span
                    :class="[
                      item.isWildcardPath
                        ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-200'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
                      'px-2 py-1 rounded-full text-xs font-medium'
                    ]"
                  >
                    {{ item.isWildcardPath ? 'Yes' : 'No' }}
                  </span>
                </td>
                <!-- Actions Column -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div class="flex items-center space-x-3">
                    <button
                      class="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
                      title="Edit filter"
                      @click="handleEditClick(item, $event)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                        />
                      </svg>
                    </button>
                    <button
                      class="text-rose-500 dark:text-red-400 hover:text-rose-600 dark:hover:text-red-300 transition-colors"
                      title="Delete filter"
                      @click="handleDeleteClick(item._id, $event)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <!-- Expanded Row -->
              <tr
                v-if="expandedRows.has(item._id)"
                class="bg-gray-50 dark:bg-gray-700/90"
              >
                <td
                  colspan="7"
                  class="px-6 py-4"
                >
                  <div class="grid grid-cols-4 gap-4">
                    <template v-if="item.filter">
                      <!-- Body Filters -->
                      <div
                        v-if="Object.keys(item.filter.body || {}).length"
                        class="space-y-2"
                      >
                        <h4 class="font-medium text-blue-700 dark:text-blue-300">Body Filters</h4>
                        <pre
                          class="p-2 rounded border text-xs overflow-auto max-h-40 bg-white dark:bg-gray-800 border-blue-200 dark:border-blue-800"
                          >{{ JSON.stringify(item.filter.body, null, 2) }}</pre
                        >
                      </div>
                      <!-- Query Filters -->
                      <div
                        v-if="Object.keys(item.filter.query || {}).length"
                        class="space-y-2"
                      >
                        <h4 class="font-medium text-green-700 dark:text-green-300">
                          Query Filters
                        </h4>
                        <pre
                          class="p-2 rounded border text-xs overflow-auto max-h-40 bg-white dark:bg-gray-800 border-green-200 dark:border-green-800"
                          >{{ JSON.stringify(item.filter.query, null, 2) }}</pre
                        >
                      </div>
                      <!-- Params Filters -->
                      <div
                        v-if="Object.keys(item.filter.params || {}).length"
                        class="space-y-2"
                      >
                        <h4 class="font-medium text-yellow-700 dark:text-yellow-300">
                          Params Filters
                        </h4>
                        <pre
                          class="p-2 rounded border text-xs overflow-auto max-h-40 bg-white dark:bg-gray-800 border-yellow-200 dark:border-yellow-800"
                          >{{ JSON.stringify(item.filter.params, null, 2) }}</pre
                        >
                      </div>
                      <!-- Headers Filters -->
                      <div
                        v-if="Object.keys(item.filter.headers || {}).length"
                        class="space-y-2"
                      >
                        <h4 class="font-medium text-purple-700 dark:text-purple-300">
                          Headers Filters
                        </h4>
                        <pre
                          class="p-2 rounded border text-xs overflow-auto max-h-40 bg-white dark:bg-gray-800 border-purple-200 dark:border-purple-800"
                          >{{ JSON.stringify(item.filter.headers, null, 2) }}</pre
                        >
                      </div>
                    </template>
                    <div
                      v-else
                      class="col-span-4 italic text-gray-500 dark:text-gray-400"
                    >
                      No filters configured
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <!-- Pagination -->
      <div
        v-if="totalPages > 1"
        :class="[
          theme.isDark.value ? 'border-gray-700' : 'border-gray-200',
          'px-4 py-3 border-t sm:px-6 flex items-center justify-between'
        ]"
      >
        <div class="flex-1 flex justify-between sm:hidden">
          <button
            :disabled="currentPage === 1"
            :class="[
              theme.isDark.value
                ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50',
              'relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md'
            ]"
            @click="currentPage--"
          >
            Previous
          </button>
          <button
            :disabled="currentPage === totalPages"
            :class="[
              theme.isDark.value
                ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50',
              'relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md'
            ]"
            @click="currentPage++"
          >
            Next
          </button>
        </div>
        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-600', 'text-sm']">
              Showing
              <span class="font-medium">{{ (currentPage - 1) * itemsPerPage + 1 }}</span>
              to
              <span class="font-medium">{{
                Math.min(currentPage * itemsPerPage, filteredData.length)
              }}</span>
              of
              <span class="font-medium">{{ filteredData.length }}</span>
              results
            </p>
          </div>
          <div>
            <nav
              class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                :disabled="currentPage === 1"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium'
                ]"
                @click="currentPage = 1"
              >
                <span class="sr-only">First</span>
                ⟪
              </button>
              <button
                :disabled="currentPage === 1"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-2 py-2 border text-sm font-medium'
                ]"
                @click="currentPage--"
              >
                <span class="sr-only">Previous</span>
                ⟨
              </button>
              <template
                v-for="page in displayedPages"
                :key="page"
              >
                <button
                  v-if="page !== '...'"
                  :class="[
                    currentPage === page
                      ? theme.isDark.value
                        ? 'z-10 bg-blue-900 border-blue-600 text-blue-300'
                        : 'z-10 bg-blue-50 border-blue-400 text-blue-500'
                      : theme.isDark.value
                        ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                  ]"
                  @click="currentPage = Number(page)"
                >
                  {{ page }}
                </button>
                <span
                  v-else
                  :class="[
                    theme.isDark.value
                      ? 'border-gray-600 bg-gray-700 text-gray-400'
                      : 'border-gray-300 bg-white text-gray-700',
                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                  ]"
                >
                  ...
                </span>
              </template>
              <button
                :disabled="currentPage === totalPages"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-2 py-2 border text-sm font-medium'
                ]"
                @click="currentPage++"
              >
                <span class="sr-only">Next</span>
                ⟩
              </button>
              <button
                :disabled="currentPage === totalPages"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                  'relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium'
                ]"
                @click="currentPage = totalPages"
              >
                <span class="sr-only">Last</span>
                ⟫
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <Toast
      v-model:show="toast.show"
      :message="toast.message"
      :type="toast.type"
      :details="toast.details"
      :duration="5000"
    />

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800 text-white' : 'bg-white text-gray-900',
            'inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full'
          ]"
        >
          <div
            :class="[
              theme.isDark.value ? 'bg-gray-800' : 'bg-white',
              'px-4 pt-5 pb-4 sm:p-6 sm:pb-4'
            ]"
          >
            <div class="sm:flex sm:items-start">
              <div
                class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10"
              >
                <svg
                  class="h-6 w-6 text-rose-500 dark:text-red-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3
                  :class="[
                    theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                    'text-lg leading-6 font-medium'
                  ]"
                >
                  Delete Filter
                </h3>
                <div class="mt-2">
                  <p :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-500', 'text-sm']">
                    Are you sure you want to delete this filter? This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div
            :class="[
              theme.isDark.value ? 'bg-gray-700' : 'bg-gray-50',
              'px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3'
            ]"
          >
            <button
              type="button"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-6 py-2.5 bg-rose-500 dark:bg-red-600 text-base font-medium text-white hover:bg-rose-600 dark:hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 dark:focus:ring-red-500 transition-colors duration-200 sm:w-auto sm:text-sm"
              @click="handleDelete"
            >
              Delete
            </button>
            <button
              type="button"
              :class="[
                theme.isDark.value
                  ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                'mt-3 w-full inline-flex justify-center rounded-md border shadow-sm px-6 py-2.5 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 sm:mt-0 sm:w-auto sm:text-sm'
              ]"
              @click="showDeleteConfirm = false"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <CreateFilterModal
      v-model="showCreateModal"
      :edit-mode="!!filterToEdit"
      :filter-data="filterToEdit"
      @created="handleCreated"
      @updated="handleEditSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, reactive, inject } from 'vue'
import { istioMeshApiClient } from '../../services/api/istioMeshApiClient'
import type { RequestFilter } from '../../types/request-filter'
import CreateFilterModal from '../../components/features/request-filter/CreateFilterModal.vue'
import Toast from '../../components/base/common/Toast.vue'
import { useTheme } from '../../utils/useTheme'

// Use theme via inject with a fallback
const theme = inject('theme', useTheme())

// Data
const requestFilters = ref<RequestFilter[]>([])
const isLoading = ref(true)
const error = ref<string | null>(null)
const expandedRows = ref<Set<string>>(new Set())

// Create Modal State
const showCreateModal = ref(false)

const openCreateModal = () => {
  showCreateModal.value = true
}

const handleCreated = () => {
  fetchFilters()
}

// Sorting
const sortField = ref('createdAt')
const sortOrder = ref<'asc' | 'desc'>('desc')

// Search
const searchQuery = ref('')

// Pagination
const currentPage = ref(1)
const itemsPerPage = ref(10)

// Toast state
const toast = reactive({
  show: false,
  message: '',
  type: 'success' as 'success' | 'error',
  details: [] as string[]
})

const showToast = (message: string, type: 'success' | 'error', details?: string[]) => {
  toast.message = message
  toast.type = type
  toast.details = details || []
  toast.show = true
}

// Delete confirmation
const showDeleteConfirm = ref(false)
const filterToDelete = ref<string | null>(null)

const handleDeleteClick = (id: string, event: Event) => {
  event.stopPropagation() // Prevent row expansion
  filterToDelete.value = id
  showDeleteConfirm.value = true
}

const handleDelete = async () => {
  if (!filterToDelete.value) return

  try {
    await istioMeshApiClient.delete(`/platform/istio/request-filter/${filterToDelete.value}`)
    showToast('Filter deleted successfully', 'success')
    fetchFilters() // Refresh the list
  } catch (error: any) {
    let errorMessage = 'Failed to delete filter'
    if (error instanceof Error) {
      errorMessage = error.message
    }
    showToast('Failed to delete filter', 'error', [errorMessage])
  } finally {
    showDeleteConfirm.value = false
    filterToDelete.value = null
  }
}

// Edit mode
const filterToEdit = ref<RequestFilter | null>(null)

const handleEditClick = (filter: RequestFilter, event: Event) => {
  event.stopPropagation() // Prevent row expansion
  filterToEdit.value = filter
  showCreateModal.value = true // Use the same modal for editing
}

const handleEditSuccess = () => {
  showToast('Filter updated successfully', 'success')
  fetchFilters() // Refresh the list
  filterToEdit.value = null
}

// Computed
const filteredData = computed(() => {
  let result = [...requestFilters.value]

  // Apply search
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((item) => {
      // Search in path and version
      if (item.path.toLowerCase().includes(query) || item.version.toLowerCase().includes(query)) {
        return true
      }

      // Deep search in filters
      if (item.filter) {
        // Search in body filters
        if (item.filter.body && JSON.stringify(item.filter.body).toLowerCase().includes(query)) {
          return true
        }
        // Search in query filters
        if (item.filter.query && JSON.stringify(item.filter.query).toLowerCase().includes(query)) {
          return true
        }
        // Search in params filters
        if (
          item.filter.params &&
          JSON.stringify(item.filter.params).toLowerCase().includes(query)
        ) {
          return true
        }
        // Search in headers filters
        if (
          item.filter.headers &&
          JSON.stringify(item.filter.headers).toLowerCase().includes(query)
        ) {
          return true
        }
      }

      return false
    })
  }

  // Apply sorting
  if (sortField.value) {
    result.sort((a, b) => {
      const aVal = a[sortField.value as keyof RequestFilter]
      const bVal = b[sortField.value as keyof RequestFilter]

      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0
      if (aVal === undefined) return 1
      if (bVal === undefined) return -1

      // Handle dates
      if (sortField.value === 'createdAt' || sortField.value === 'updatedAt') {
        const aDate = new Date(aVal as string).getTime()
        const bDate = new Date(bVal as string).getTime()
        const comparison = aDate > bDate ? 1 : -1
        return sortOrder.value === 'asc' ? comparison : -comparison
      }

      // Handle other fields
      const comparison = String(aVal) > String(bVal) ? 1 : -1
      return sortOrder.value === 'asc' ? comparison : -comparison
    })
  }

  return result
})

const totalPages = computed(() => Math.ceil(filteredData.value.length / itemsPerPage.value))

const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredData.value.slice(start, end)
})

const displayedPages = computed(() => {
  const total = totalPages.value
  const current = currentPage.value
  const range: (number | string)[] = []

  if (total <= 7) {
    for (let i = 1; i <= total; i++) range.push(i)
  } else {
    if (current <= 3) {
      for (let i = 1; i <= 5; i++) range.push(i)
      range.push('...', total)
    } else if (current >= total - 2) {
      range.push(1, '...')
      for (let i = total - 4; i <= total; i++) range.push(i)
    } else {
      range.push(1, '...')
      for (let i = current - 1; i <= current + 1; i++) range.push(i)
      range.push('...', total)
    }
  }

  return range
})

// Table configuration
const columns = [
  {
    field: 'path',
    label: 'Path',
    sortable: true,
    searchable: true
  },
  {
    field: 'version',
    label: 'Version',
    sortable: true,
    searchable: true
  },
  {
    field: 'filter',
    label: 'Filters',
    sortable: false
  },
  {
    field: 'createdAt',
    label: 'Created',
    sortable: true
  },
  {
    field: 'updatedAt',
    label: 'Last Updated',
    sortable: true
  },
  {
    field: 'isWildcardPath',
    label: 'Wildcard Path',
    sortable: true
  },
  {
    field: 'actions',
    label: 'Actions',
    sortable: false
  }
]

// Helper Functions
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString()
}

const toggleExpand = (id: string) => {
  if (expandedRows.value.has(id)) {
    expandedRows.value.delete(id)
  } else {
    expandedRows.value.add(id)
  }
}

// Methods
const handleSort = (field: string) => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortOrder.value = 'asc'
  }
}

// Watch for search changes to reset pagination
watch(searchQuery, () => {
  currentPage.value = 1
  expandedRows.value.clear()
})

// API Methods
const fetchFilters = async () => {
  try {
    isLoading.value = true
    const response = await istioMeshApiClient.get<RequestFilter[]>('/platform/istio/request-filter')
    requestFilters.value = response
  } catch (err) {
    error.value = 'Failed to load request filters'
  } finally {
    isLoading.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchFilters()
})
</script>

<style scoped>
pre {
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
}
</style>
